// ProjectsPage — full CRUD via /api/projects
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Plus, MoreVertical, Pencil, Loader2, X, Save, FolderOpen, ChevronRight, UploadCloud, SlidersHorizontal, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import Pill from "@/components/Pill";
import ProjectContactsEditor from "@/components/ProjectContactsEditor";
import { WsrReviewerPicker } from "@/components/ClientFormFields";
import ProjectsOverview, { stageBucket } from "@/components/ProjectsOverview";

const PROJECT_STATUSES = ["Active", "In Progress", "On Site", "On Hold", "Partial Completion", "Handover", "Complete", "Cancelled"];
export const STATUS_TONE = { "Active": "blue", "In Progress": "accent", "On Site": "purple", "On Hold": "amber", "Partial Completion": "amber", "Handover": "purple", "Complete": "green", "Cancelled": "neutral" };
const STAGE_LABELS = {
    enquiry: "Enquiry", setup: "Setup", pre_construction: "Pre-Construction",
    site_visits: "Site Visits", reporting: "Reporting", qa: "QA / Review",
    client_issue: "Client Issue", completion: "Completion",
};
const STAGE_ORDER = ["enquiry", "setup", "pre_construction", "site_visits", "reporting", "qa", "client_issue", "completion"];
const STAGE_TONE = { qa: "amber", client_issue: "purple", completion: "green", site_visits: "blue", reporting: "blue" };
const RIBA_STAGES = ["N/A", "0", "1", "2", "3", "4", "5", "6", "7"];
const CDM_ROLES = ["N/A", "Principal Designer", "Designer", "Principal Contractor", "Contractor", "Client", "General Health and Safety Advice"];

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

export default function ProjectsPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);   // Settings → Project Types picklist
    const [q, setQ] = useState("");
    const [statusFilters, setStatusFilters] = useState([]);
    const [stageFilters, setStageFilters] = useState([]);
    const [consultantFilters, setConsultantFilters] = useState([]);
    const [typeFilters, setTypeFilters] = useState([]);
    const [flagFilters, setFlagFilters] = useState([]); // any of: Blocked, Overdue
    const [clientFilter, setClientFilter] = useState(null); // internal client-chip filter
    const [bucketFilter, setBucketFilter] = useState(null); // pipeline-stage bucket filter
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [menuRowId, setMenuRowId] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkField, setBulkField] = useState("status");
    const [bulkValue, setBulkValue] = useState("");
    const { user } = useAuth();
    const { portalClientId, isInternalView, portalClient } = useView();
    const hideFin = !isInternalView && !!portalClient?.hide_financials;
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const canInlineEdit = canEdit || user?.role === "client_admin";  // Client Admin: Type/Lead/Stage/Target/Status only, never Value

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const p = portalClientId && !isInternalView ? { client_id: portalClientId } : {};
            const { data } = await api.get("/projects", { params: p });
            setRows(data);
        } finally {
            setLoading(false);
        }
    }, [portalClientId, isInternalView]);

    useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
    useEffect(() => { api.get("/project-types").then((r) => setTypeOptions(r.data)).catch(() => setTypeOptions([])); }, []);
    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit) {
            setDialog({ open: true, mode: "create", initial: null });
            searchParams.delete("new"); setSearchParams(searchParams, { replace: true });
        }
        const stageParam = searchParams.get("stage");
        if (stageParam) setStageFilters([stageParam]);
        const flagParam = searchParams.get("flag");
        if (flagParam) setFlagFilters([flagParam]);
    }, [searchParams, canEdit, setSearchParams]);

    useEffect(() => {
        const editId = searchParams.get("edit");
        if (!editId || !rows.length) return;
        const proj = rows.find((r) => r.id === editId);
        if (proj) setDialog({ open: true, mode: "edit", initial: proj });
        searchParams.delete("edit"); setSearchParams(searchParams, { replace: true });
    }, [searchParams, rows, setSearchParams]);

    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const typeNames = typeOptions.map((t) => t.name);
    const today = new Date().toISOString().slice(0, 10);
    const consultants = Array.from(new Set(rows.map((r) => r.lead_consultant).filter(Boolean))).sort();
    const isBlocked = (p) => (p.stages || []).some((s) => s.status === "Blocked");
    const isOverdue = (p) => p.target_date && p.target_date < today && p.status !== "Complete" && p.status !== "Cancelled";
    const filtered = rows.filter((p) => {
        if (statusFilters.length && !statusFilters.includes(p.status)) return false;
        if (stageFilters.length && !stageFilters.includes(p.workflow_stage)) return false;
        if (consultantFilters.length && !consultantFilters.includes(p.lead_consultant)) return false;
        if (typeFilters.length && !(p.project_types || []).some((t) => typeFilters.includes(t))) return false;
        if (clientFilter && p.client_id !== clientFilter) return false;
        if (bucketFilter && stageBucket(p.workflow_stage) !== bucketFilter) return false;
        if (flagFilters.length) {
            const matches = (flagFilters.includes("Blocked") && isBlocked(p)) || (flagFilters.includes("Overdue") && isOverdue(p));
            if (!matches) return false;
        }
        if (q && !`${p.name} ${p.reference} ${clientMap[p.client_id]?.name || ""} ${p.lead_consultant || ""}`
            .toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    // Distinct clients present in the loaded rows (for the internal client-chip bar)
    const presentClients = Array.from(new Set(rows.map((r) => r.client_id)))
        .map((id) => clientMap[id]).filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    const groupDonutByClient = isInternalView && presentClients.length > 1 && !clientFilter;

    const hasActiveFilters = q || statusFilters.length || stageFilters.length || consultantFilters.length || typeFilters.length || flagFilters.length || clientFilter || bucketFilter;
    const clearFilters = () => {
        setQ(""); setStatusFilters([]); setStageFilters([]); setConsultantFilters([]);
        setTypeFilters([]); setFlagFilters([]); setClientFilter(null); setBucketFilter(null);
    };
    // Filters consolidated into the popover (status / stage / consultant / type / flag) — each supports selecting multiple values
    const popoverFilterCount = statusFilters.length + stageFilters.length + consultantFilters.length + typeFilters.length + flagFilters.length;
    const clearPopoverFilters = () => {
        setStatusFilters([]); setStageFilters([]); setConsultantFilters([]); setTypeFilters([]); setFlagFilters([]);
    };

    // "Projects & Delivery" (dashboard + timeline) vs "Project lists" (the
    // detailed filterable table) — a tab switcher on this one page, not two
    // separate routes, so all the shared filter/edit state above stays in
    // one place. Backed by a query param so a tab can be linked to directly.
    const activeTab = searchParams.get("tab") === "list" ? "list" : "delivery";
    const setActiveTab = (tab) => {
        const next = new URLSearchParams(searchParams);
        if (tab === "list") next.set("tab", "list"); else next.delete("tab");
        setSearchParams(next);
    };
    // Clicking a dashboard segment (client donut / pipeline bucket) filters the
    // table, so jump straight to the Project lists tab to see the result.
    const filterClientAndShowList = (cid) => { setClientFilter(cid); setActiveTab("list"); };
    const filterBucketAndShowList = (b) => { setBucketFilter(bucketFilter === b ? null : b); setActiveTab("list"); };

    const accessors = useMemo(() => ({
        name: (p) => p.name,
        client: (p) => clientMap[p.client_id]?.name || "",
        project_type: (p) => (p.project_types?.length ? p.project_types.join(", ") : (p.project_type || "")),
        lead_consultant: (p) => p.lead_consultant || "",
        stage: (p) => STAGE_ORDER.indexOf(p.workflow_stage),
        target_date: (p) => p.target_date || "",
        value_gbp: (p) => p.value_gbp || 0,
        status: (p) => p.status,
    }), [clientMap]); // eslint-disable-line react-hooks/exhaustive-deps
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, { accessors });

    // Inline / bulk editing (internal staff use the full endpoint; Client Admin's
    // inline edits from this list go through the restricted portal-details endpoint)
    const patchProject = useCallback(async (id, patch) => {
        setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));  // optimistic
        const endpoint = canEdit ? `/projects/${id}` : `/projects/${id}/portal-details`;
        try {
            const { data } = await api.patch(endpoint, patch);
            setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
            refresh();
        }
    }, [refresh, canEdit]);

    const visibleIds = sorted.map((p) => p.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visibleIds));
    const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const BULK_FIELDS = [
        ["status", "Status"], ["workflow_stage", "Stage"], ["project_types", "Type"],
        ["lead_consultant", "Lead"], ["target_date", "Target date"], ["value_gbp", "Value (£)"],
    ];
    async function applyBulk() {
        if (selected.size === 0) return;
        let coerced = bulkValue;
        if (bulkField === "value_gbp") coerced = bulkValue === "" ? null : Number(bulkValue);
        if (bulkField === "lead_consultant") coerced = bulkValue || null;
        if (bulkField === "project_types") {
            if (!Array.isArray(bulkValue) || bulkValue.length === 0) { toast.error("Choose at least one type to apply"); return; }
        } else if (coerced === "" && bulkField !== "value_gbp") { toast.error("Choose a value to apply"); return; }
        const ids = [...selected];
        setRows((rs) => rs.map((r) => (ids.includes(r.id) ? { ...r, [bulkField]: coerced } : r)));
        await Promise.all(ids.map((id) => api.patch(`/projects/${id}`, { [bulkField]: coerced }).catch(() => null)));
        toast.success(`Updated ${ids.length} project${ids.length > 1 ? "s" : ""}`);
        setSelected(new Set()); setBulkValue(""); refresh();
    }
    const colSpan = (editMode && canEdit ? 10 : 9) - (hideFin ? 1 : 0);

    const [exporting, setExporting] = useState(false);
    async function exportToExcel() {
        if (!filtered.length) { toast.error("No projects to export — adjust your filters first."); return; }
        setExporting(true);
        try {
            const res = await api.post("/projects/export", { project_ids: filtered.map((p) => p.id) }, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `projects-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Exported ${filtered.length} project${filtered.length === 1 ? "" : "s"} to Excel.`);
        } catch (e) {
            toast.error("Could not export projects — please try again.");
        } finally { setExporting(false); }
    }

    return (
        <div data-testid="projects-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="flex items-center gap-4" data-testid="projects-tabs">
                        <button type="button" data-testid="projects-tab-delivery" onClick={() => setActiveTab("delivery")}
                            className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase transition-colors"
                            style={{ borderBottom: activeTab === "delivery" ? "2px solid var(--brand-accent, #C75A1B)" : "2px solid transparent", color: activeTab === "delivery" ? "#6F6A64" : "#B7AC93" }}>
                            Projects & Delivery
                        </button>
                        <button type="button" data-testid="projects-tab-list" onClick={() => setActiveTab("list")}
                            className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase transition-colors"
                            style={{ borderBottom: activeTab === "list" ? "2px solid var(--brand-accent, #C75A1B)" : "2px solid transparent", color: activeTab === "list" ? "#6F6A64" : "#B7AC93" }}>
                            Project lists
                        </button>
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Projects</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} projects · {rows.filter(r => r.status && !["Complete", "Cancelled"].includes(r.status)).length} active · {rows.filter(r => r.status === "On Hold").length} on hold
                        {(() => { if (hideFin) return ""; const t = rows.reduce((s, r) => s + (r.value_gbp || 0), 0); return t ? ` · £${t >= 10000 ? Math.round(t / 1000) + "k" : t.toLocaleString()} value` : ""; })()}
                    </p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        {isInternalView && (
                            <button data-testid="projects-import-button" onClick={() => navigate("/app/projects/import")}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]"
                                style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                                <UploadCloud className="w-3.5 h-3.5" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Import
                            </button>
                        )}
                        {isInternalView && (
                            <button data-testid="projects-import-tracker-button" onClick={() => navigate("/app/projects/import-tracker")}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]"
                                style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                                <UploadCloud className="w-3.5 h-3.5" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Import tracker
                            </button>
                        )}
                        <button data-testid="projects-new-button"
                            onClick={() => setDialog({ open: true, mode: "create", initial: null })}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white transition-colors"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#9F4212")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand-accent, #C75A1B)")}>
                            <Plus className="w-3.5 h-3.5" /> New project
                        </button>
                    </div>
                )}
            </div>

            {activeTab === "delivery" && (
            <ProjectsOverview projects={filtered} clientMap={clientMap} groupByClient={groupDonutByClient} loading={loading} hideFinancials={hideFin}
                onOpen={(pid) => navigate(`/app/projects/${pid}`)}
                onFilterClient={filterClientAndShowList}
                onFilterBucket={filterBucketAndShowList} />
            )}

            {activeTab === "list" && (
            <>
            {(clientFilter || bucketFilter || popoverFilterCount > 0) && (
                <div className="flex items-center gap-2 text-[11.5px] flex-wrap" style={{ color: "#3e4b63" }} data-testid="projects-active-filters">
                    <span className="font-semibold">Filtered:</span>
                    {clientFilter && <button onClick={() => setClientFilter(null)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>{clientMap[clientFilter]?.name} <X className="w-3 h-3" /></button>}
                    {bucketFilter && <button onClick={() => setBucketFilter(null)} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>{bucketFilter} <X className="w-3 h-3" /></button>}
                    {statusFilters.map((v) => <button key={`s-${v}`} data-testid={`projects-chip-status-${v}`} onClick={() => setStatusFilters((cur) => cur.filter((x) => x !== v))} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>{v} <X className="w-3 h-3" /></button>)}
                    {stageFilters.map((v) => <button key={`st-${v}`} data-testid={`projects-chip-stage-${v}`} onClick={() => setStageFilters((cur) => cur.filter((x) => x !== v))} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>{STAGE_LABELS[v] || v} <X className="w-3 h-3" /></button>)}
                    {consultantFilters.map((v) => <button key={`c-${v}`} data-testid={`projects-chip-consultant-${v}`} onClick={() => setConsultantFilters((cur) => cur.filter((x) => x !== v))} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>{v} <X className="w-3 h-3" /></button>)}
                    {typeFilters.map((v) => <button key={`t-${v}`} data-testid={`projects-chip-type-${v}`} onClick={() => setTypeFilters((cur) => cur.filter((x) => x !== v))} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>{v} <X className="w-3 h-3" /></button>)}
                    {flagFilters.map((v) => <button key={`f-${v}`} data-testid={`projects-chip-flag-${v}`} onClick={() => setFlagFilters((cur) => cur.filter((x) => x !== v))} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>{v} <X className="w-3 h-3" /></button>)}
                </div>
            )}

            <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex items-center gap-2 bg-[#F6F4F0] border rounded-[8px] px-3 py-1.5 flex-1 min-w-[220px] max-w-[340px]"
                         style={{ borderColor: "#E5E0DA" }}>
                        <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, ref, client, consultant…"
                            className="flex-1 bg-transparent outline-none text-[12.5px]"
                            data-testid="projects-search" />
                    </div>

                    <div className="relative">
                        <button data-testid="projects-filters-button" onClick={() => setFiltersOpen((v) => !v)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold border transition-colors ${popoverFilterCount > 0 ? "text-white border-transparent" : "bg-white hover:bg-[#F6F4F0]"}`}
                            style={popoverFilterCount > 0 ? { background: "#1f2a44" } : { borderColor: "#E5E0DA", color: "#3e4b63" }}>
                            <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
                            {popoverFilterCount > 0 && (
                                <span data-testid="projects-filters-count" className="ml-0.5 text-[10px] font-bold px-1.5 py-px rounded-full" style={{ background: "#fff", color: "#1f2a44" }}>{popoverFilterCount}</span>
                            )}
                        </button>
                        {filtersOpen && (
                            <>
                                <div className="fixed inset-0 z-30" onClick={() => setFiltersOpen(false)} />
                                <div data-testid="projects-filters-popover"
                                    className="absolute z-40 mt-2 left-0 w-[300px] bg-white border rounded-xl shadow-brand p-3.5 space-y-3 animate-fade-in"
                                    style={{ borderColor: "#E5E0DA" }}>
                                    <FilterMultiSelect label="Status" testId="projects-status-filter" selected={statusFilters} onChange={setStatusFilters}
                                        options={PROJECT_STATUSES.map((s) => [s, s])} />
                                    <FilterMultiSelect label="Stage" testId="projects-stage-filter" selected={stageFilters} onChange={setStageFilters}
                                        options={STAGE_ORDER.map((s) => [s, STAGE_LABELS[s]])} />
                                    <FilterMultiSelect label="Consultant" testId="projects-consultant-filter" selected={consultantFilters} onChange={setConsultantFilters}
                                        options={consultants.map((c) => [c, c])} />
                                    <FilterMultiSelect label="Type" testId="projects-type-filter" selected={typeFilters} onChange={setTypeFilters}
                                        options={typeNames.map((t) => [t, t])} />
                                    <FilterMultiSelect label="Flag" testId="projects-flag-filter" selected={flagFilters} onChange={setFlagFilters}
                                        options={[["Blocked", "Blocked"], ["Overdue", "Overdue"]]} />
                                    <div className="flex items-center gap-2 pt-1">
                                        <button data-testid="projects-filters-clear" onClick={clearPopoverFilters}
                                            className="flex-1 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#3e4b63" }}>
                                            Clear
                                        </button>
                                        <button data-testid="projects-filters-apply" onClick={() => setFiltersOpen(false)}
                                            className="flex-1 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="ml-auto text-[11.5px] flex items-center gap-3" style={{ color: "#6F6A64" }}>
                        <button data-testid="projects-export-excel" onClick={exportToExcel} disabled={exporting}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold border bg-white hover:bg-[#F6F4F0] disabled:opacity-60"
                            style={{ borderColor: "#E5E0DA", color: "#3e4b63" }}>
                            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" style={{ color: "var(--brand-accent,#C75A1B)" }} />}
                            Export to Excel
                        </button>
                        {canInlineEdit && (
                            <button data-testid="projects-edit-toggle" onClick={() => { setEditMode((v) => !v); setSelected(new Set()); }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold border transition-colors ${editMode ? "text-white border-transparent" : "bg-white hover:bg-[#F6F4F0]"}`}
                                style={editMode ? { background: "var(--brand-accent, #C75A1B)" } : { borderColor: "#E5E0DA", color: "#3e4b63" }}>
                                <Pencil className="w-3.5 h-3.5" /> {editMode ? "Done" : "Edit list"}
                            </button>
                        )}
                        <span>{filtered.length} shown · {rows.length} total</span>
                    </div>
                </div>

                {editMode && canEdit && selected.size > 0 && (
                    <div data-testid="projects-bulk-bar" className="flex items-center gap-2 px-4 py-2.5 border-b flex-wrap" style={{ borderColor: "#F0EDE8", background: "#FBF6EF" }}>
                        <span className="text-[12px] font-bold" style={{ color: "#9F4212" }}>{selected.size} selected</span>
                        <span className="text-[11.5px]" style={{ color: "#6F6A64" }}>· set</span>
                        <select data-testid="projects-bulk-field" value={bulkField} onChange={(e) => { setBulkField(e.target.value); setBulkValue(e.target.value === "project_types" ? [] : ""); }}
                            className="bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none" style={{ borderColor: "#E5E0DA" }}>
                            {BULK_FIELDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        <span className="text-[11.5px]" style={{ color: "#6F6A64" }}>to</span>
                        <BulkValueControl field={bulkField} value={bulkValue} onChange={setBulkValue} consultants={consultants} typeNames={typeNames} />
                        <button data-testid="projects-bulk-apply" onClick={applyBulk}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-white" style={{ background: "#1f2a44" }}>
                            Apply to {selected.size}
                        </button>
                        <button data-testid="projects-bulk-clear" onClick={() => setSelected(new Set())}
                            className="text-[12px] font-semibold px-2 py-1.5 rounded-[8px] hover:bg-white" style={{ color: "#6F6A64" }}>
                            Clear selection
                        </button>
                    </div>
                )}

                <table className="w-full text-[12.7px]">
                    <thead>
                        <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                            {editMode && canEdit && (
                                <th className="pl-4 w-9">
                                    <input type="checkbox" data-testid="projects-select-all" checked={allSelected} onChange={toggleAll} />
                                </th>
                            )}
                            <SortableTh myKey="name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-name" className="group">Project</SortableTh>
                            <SortableTh myKey="client" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-client" className="group">Client</SortableTh>
                            <SortableTh myKey="project_type" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-type" className="group">Type</SortableTh>
                            <SortableTh myKey="lead_consultant" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-lead" className="group">Lead</SortableTh>
                            <SortableTh myKey="stage" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-stage" className="group">Stage</SortableTh>
                            <SortableTh myKey="target_date" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-target" className="group">Target</SortableTh>
                            {!hideFin && <SortableTh myKey="value_gbp" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-value" className="group">Value</SortableTh>}
                            <SortableTh myKey="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="projects-th-status" className="group">Status</SortableTh>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <tr key={i} className="border-t animate-pulse" style={{ borderColor: "#F0EDE8" }} data-testid="projects-row-skeleton">
                                    {editMode && <td className="pl-4 w-9"><div className="h-3 w-3 rounded bg-[#ECE7E0]" /></td>}
                                    <td className="px-5 py-3.5"><div className="h-3 w-40 rounded bg-[#ECE7E0]" /><div className="h-2 w-24 rounded bg-[#F1ECE5] mt-2" /></td>
                                    <td className="px-3 py-3.5"><div className="h-3 w-28 rounded bg-[#ECE7E0]" /></td>
                                    <td className="px-3 py-3.5"><div className="h-3 w-20 rounded bg-[#ECE7E0]" /></td>
                                    <td className="px-3 py-3.5"><div className="h-3 w-24 rounded bg-[#ECE7E0]" /></td>
                                    <td className="px-3 py-3.5"><div className="h-4 w-16 rounded-full bg-[#ECE7E0]" /></td>
                                    <td className="px-3 py-3.5"><div className="h-3 w-20 rounded bg-[#ECE7E0]" /></td>
                                    <td className="px-3 py-3.5"><div className="h-3 w-16 rounded bg-[#ECE7E0]" /></td>
                                    <td className="px-3 py-3.5"><div className="h-4 w-14 rounded-full bg-[#ECE7E0]" /></td>
                                    <td></td>
                                </tr>
                            ))
                        ) : sorted.map((p) => {
                            const c = clientMap[p.client_id];
                            return (
                                <tr key={p.id} data-testid={`project-row-${p.id}`}
                                    onClick={() => { if (!editMode) navigate(`/app/projects/${p.id}`); }}
                                    className={`border-t hover:bg-[#FCFBF9] ${editMode ? "" : "cursor-pointer"} ${selected.has(p.id) ? "bg-[#FBF6EF]" : ""}`} style={{ borderColor: "#F0EDE8" }}>
                                    {editMode && canEdit && (
                                        <td className="pl-4 w-9" onClick={(e) => e.stopPropagation()}>
                                            <input type="checkbox" data-testid={`project-select-${p.id}`} checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} />
                                        </td>
                                    )}
                                    <td className="px-5 py-3">
                                        <div className="font-semibold text-[#1F1F1F] flex items-center gap-1.5">{p.name}
                                            {isOverdue(p) && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#FBE8E5] text-[#B33A2E]">Overdue</span>}
                                            {isBlocked(p) && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-[#FBE8E5] text-[#B33A2E]">Blocked</span>}
                                        </div>
                                        <div className="text-[10.5px] font-mono" style={{ color: "#9A968F" }}>
                                            {p.reference}{p.f10_number ? <> · F10 {p.f10_number}</> : null}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        {c ? (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="w-5 h-5 rounded-[4px] grid place-items-center text-[9px] font-bold text-white"
                                                      style={{ background: c.branding.primary_colour }}>
                                                    {c.branding.logo_initials}
                                                </span>
                                                <span className="text-[#2D2D2D]">{c.name}</span>
                                            </span>
                                        ) : <span className="italic text-[#9A968F]">—</span>}
                                    </td>
                                    {editMode ? (
                                        <>
                                            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                                <TypeMultiSelect testId={`project-edit-type-${p.id}`} options={typeNames}
                                                    selected={p.project_types || []} onChange={(v) => patchProject(p.id, { project_types: v })} />
                                            </td>
                                            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                                <CellInput testId={`project-edit-lead-${p.id}`} value={p.lead_consultant || ""} onCommit={(v) => patchProject(p.id, { lead_consultant: v || null })} placeholder="unassigned" />
                                            </td>
                                            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                                <CellSelect testId={`project-edit-stage-${p.id}`} value={p.workflow_stage || "enquiry"} onChange={(v) => patchProject(p.id, { workflow_stage: v })}
                                                    options={STAGE_ORDER.map((s) => [s, STAGE_LABELS[s]])} />
                                            </td>
                                            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                                <CellInput testId={`project-edit-target-${p.id}`} type="date" value={p.target_date || ""} onCommit={(v) => patchProject(p.id, { target_date: v || null })} />
                                            </td>
                                            {!hideFin && (
                                                <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                                    {canEdit ? (
                                                        <CellInput testId={`project-edit-value-${p.id}`} type="number" value={p.value_gbp ?? ""} onCommit={(v) => patchProject(p.id, { value_gbp: v === "" ? null : Number(v) })} prefix="£" />
                                                    ) : (
                                                        <span className="text-[#2D2D2D] font-mono">{p.value_gbp ? `£${(p.value_gbp).toLocaleString()}` : "—"}</span>
                                                    )}
                                                </td>
                                            )}
                                            <td className="px-2 py-2" onClick={(e) => e.stopPropagation()}>
                                                <CellSelect testId={`project-edit-status-${p.id}`} value={p.status || "Active"} onChange={(v) => patchProject(p.id, { status: v })}
                                                    options={PROJECT_STATUSES.map((s) => [s, s])} />
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-3 py-3 text-[#2D2D2D]">
                                                {p.project_types?.length ? (
                                                    <div className="flex items-center gap-1 flex-wrap max-w-[160px]">
                                                        {p.project_types.map((t, i) => (
                                                            <span key={`${p.id}-${t}-${i}`} className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full bg-[#F6F4F0] text-[#3e4b63]">{t}</span>
                                                        ))}
                                                    </div>
                                                ) : "—"}
                                            </td>
                                            <td className="px-3 py-3 text-[#2D2D2D]">{p.lead_consultant || <span className="italic text-[#9A968F]">unassigned</span>}</td>
                                            <td className="px-3 py-3">
                                                <Pill tone={STAGE_TONE[p.workflow_stage] || "neutral"}>{STAGE_LABELS[p.workflow_stage] || p.workflow_stage}</Pill>
                                            </td>
                                            <td className="px-3 py-3 text-[#2D2D2D] font-mono text-[11.5px]">{p.target_date || "—"}</td>
                                            {!hideFin && <td className="px-3 py-3 text-[#2D2D2D] font-mono">{p.value_gbp ? `£${(p.value_gbp).toLocaleString()}` : "—"}</td>}
                                            <td className="px-3 py-3"><Pill tone={STATUS_TONE[p.status] || "neutral"}>{p.status}</Pill></td>
                                        </>
                                    )}
                                    <td className="px-3 py-3 text-right relative" onClick={(e) => e.stopPropagation()}>
                                        {canEdit ? (
                                            <>
                                                <button
                                                    data-testid={`project-row-menu-${p.id}`}
                                                    onClick={() => setMenuRowId(menuRowId === p.id ? null : p.id)}
                                                    className="w-8 h-8 rounded-[7px] grid place-items-center hover:bg-[#F6F4F0]">
                                                    <MoreVertical className="w-4 h-4" style={{ color: "#6F6A64" }} />
                                                </button>
                                                {menuRowId === p.id && (
                                                    <div className="absolute right-3 top-[80%] bg-white border rounded-lg shadow-brand min-w-[180px] py-1 z-30"
                                                         style={{ borderColor: "#E5E0DA" }}>
                                                        <button onClick={() => { navigate(`/app/projects/${p.id}`); setMenuRowId(null); }}
                                                            className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0]">
                                                            <ChevronRight className="w-3.5 h-3.5" /> Open workflow
                                                        </button>
                                                        <button onClick={() => { setDialog({ open: true, mode: "edit", initial: p }); setMenuRowId(null); }}
                                                            className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0]">
                                                            <Pencil className="w-3.5 h-3.5" /> Edit details
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        ) : <ChevronRight className="w-4 h-4 inline" style={{ color: "#C8C2B8" }} />}
                                    </td>
                                </tr>
                            );
                        })}
                        {!loading && sorted.length === 0 && (
                            <tr><td colSpan={colSpan} className="px-5 py-14 text-center" data-testid="projects-empty-state">
                                <FolderOpen className="w-7 h-7 mx-auto mb-3" style={{ color: "#C8C2B8" }} />
                                {hasActiveFilters ? (
                                    <>
                                        <div className="text-[14px] font-semibold text-[#1F1F1F]">No projects match your filters</div>
                                        <div className="text-[12.5px] mt-1" style={{ color: "#6F6A64" }}>Try widening or clearing the current filters.</div>
                                        <button data-testid="projects-empty-clear-filters" onClick={clearFilters}
                                            className="inline-flex items-center gap-1.5 mt-4 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white hover:bg-[#F6F4F0]"
                                            style={{ borderColor: "#E5E0DA", color: "#1f2a44" }}>
                                            <X className="w-3.5 h-3.5" /> Clear filters
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-[14px] font-semibold text-[#1F1F1F]">No projects yet</div>
                                        <div className="text-[12.5px] mt-1" style={{ color: "#6F6A64" }}>{canEdit ? "Create your first project to start tracking delivery." : "Projects published to your portal will appear here."}</div>
                                        {canEdit && (
                                            <button data-testid="projects-empty-create" onClick={() => setDialog({ open: true, mode: "create", initial: null })}
                                                className="inline-flex items-center gap-1.5 mt-4 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                                                style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                                <Plus className="w-3.5 h-3.5" /> New project
                                            </button>
                                        )}
                                    </>
                                )}
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
            </>
            )}

            <ProjectDialog open={dialog.open} mode={dialog.mode} initial={dialog.initial}
                clients={clients} onClose={() => setDialog((d) => ({ ...d, open: false }))}
                onSaved={refresh} />
        </div>
    );
}

function ProjectLogoField({ projectId, hasLogo, onChange }) {
    const [busy, setBusy] = useState(false);
    const [previewTick, setPreviewTick] = useState(0);

    const upload = async (file) => {
        setBusy(true);
        const fd = new FormData(); fd.append("file", file);
        try {
            const { data } = await api.post(`/projects/${projectId}/upload-logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.logo_url);
            setPreviewTick((t) => t + 1);
            toast.success("Logo uploaded");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Logo upload failed"); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        setBusy(true);
        try {
            await api.delete(`/projects/${projectId}/logo`);
            onChange(null);
            toast.success("Logo removed");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Remove failed"); }
        finally { setBusy(false); }
    };

    return (
        <Field label="Project logo (used on Weekly Site Report exports, alongside the client logo)">
            <div className="flex items-center gap-3">
                {hasLogo ? (
                    <img key={previewTick} data-testid="project-logo-preview" src={`${API}/projects/${projectId}/logo?t=${previewTick}`}
                         alt="Project logo" className="w-16 h-16 object-contain rounded-[8px] border" style={{ borderColor: "#E5E0DA" }} />
                ) : (
                    <div className="w-16 h-16 rounded-[8px] border grid place-items-center text-[10px] italic text-[#9A968F]" style={{ borderColor: "#E5E0DA" }}>No logo</div>
                )}
                <label className="px-3 py-2 rounded-[8px] text-[12px] font-semibold border bg-white cursor-pointer hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                    {busy ? "Uploading…" : "Upload logo"}
                    <input data-testid="project-logo-input" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                           className="hidden" disabled={busy} onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }} />
                </label>
                {hasLogo && (
                    <button data-testid="project-logo-remove" type="button" onClick={remove} disabled={busy}
                            className="text-[12px] font-semibold text-[#B33A2E] hover:underline disabled:opacity-50">Remove</button>
                )}
            </div>
        </Field>
    );
}

export function ProjectDialog({ open, mode = "create", initial, clients, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [sites, setSites] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [typeOptions, setTypeOptions] = useState([]);

    useEffect(() => {
        setForm(initial ? { ...initial, project_types: initial.project_types?.length ? initial.project_types : (initial.project_type ? [initial.project_type] : []) } : {
            name: "", client_id: "", reference: "", project_types: [],
            riba_stage: "N/A", cdm_role: "N/A", f10_number: "",
            value_gbp: "", status: "Active", start_date: "", target_date: "",
            lead_consultant: "", notes: "", template_id: "",
            contract_reference: "", client_representative: "", wsr_distribution: "",
            site_ids: [], building_ids: [], contacts: [],
        });
    }, [initial, open]);

    // Load templates + the Project Types picklist once when the dialog opens
    useEffect(() => {
        if (open) api.get("/project-types").then((r) => setTypeOptions(r.data)).catch(() => setTypeOptions([]));
    }, [open]);
    useEffect(() => {
        if (open && mode === "create") {
            api.get("/project-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
        }
    }, [open, mode]);

    // Load the chosen client's sites & buildings for the multi-selects
    const clientId = form.client_id;
    useEffect(() => {
        if (!open || !clientId) { setSites([]); setBuildings([]); return; }
        api.get("/sites", { params: { client_id: clientId } }).then((r) => setSites(r.data)).catch(() => setSites([]));
        api.get("/buildings", { params: { client_id: clientId } }).then((r) => setBuildings(r.data)).catch(() => setBuildings([]));
    }, [open, clientId]);

    if (!open) return null;

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }
    function toggleId(key, id) {
        setForm((f) => {
            const cur = new Set(f[key] || []);
            cur.has(id) ? cur.delete(id) : cur.add(id);
            return { ...f, [key]: [...cur] };
        });
    }

    async function save() {
        if (!form.name?.trim()) { toast.error("Project name is required"); return; }
        if (!form.client_id) { toast.error("Please choose a client"); return; }
        const cleaned = Object.fromEntries(
            Object.entries(form).filter(([, v]) => !(typeof v === "string" && v.trim() === ""))
        );
        const bufferedContacts = Array.isArray(cleaned.contacts) ? cleaned.contacts : [];
        delete cleaned.contacts;  // contacts are saved against the project, not on the project doc
        delete cleaned.logo_url;  // logo is uploaded via its own endpoint, never through this generic PATCH
        if (cleaned.value_gbp != null && cleaned.value_gbp !== "") {
            cleaned.value_gbp = Number(cleaned.value_gbp);
        }
        if (mode === "edit") delete cleaned.template_id;  // template can't be changed after creation
        setBusy(true);
        try {
            if (mode === "create") {
                const { data } = await api.post("/projects", cleaned);
                // persist any contacts collected in the wizard
                for (const c of bufferedContacts) {
                    try {
                        await api.post(`/projects/${data.id}/contacts`, {
                            role: c.role, name: c.name, email: c.email || null, phone: c.phone || null,
                            organisation: c.organisation || null, access_required: !!c.access_required,
                            info_only: !!c.info_only, notes: c.notes || null,
                        });
                    } catch { /* skip a bad contact, don't fail the project */ }
                }
                toast.success(`Created ${data.name} (${data.reference})`);
            } else {
                const { data } = await api.patch(`/projects/${initial.id}`, cleaned);
                toast.success(`Updated ${data.name}`);
            }
            onSaved?.();
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setBusy(false);
        }
    }

    const heading = mode === "create" ? "New project" : `Edit ${initial?.name}`;
    const chosenTemplate = templates.find((t) => t.id === form.template_id);

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in"
             style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="project-form-dialog"
                className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[680px] max-h-[92vh] flex flex-col border"
                style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center"
                         style={{ background: "#E7EEF9", color: "#2A5DB0" }}>
                        <FolderOpen className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-[19px] font-bold text-[#1F1F1F]">{heading}</h2>
                        <p className="text-[12px] mt-0.5" style={{ color: "#6F6A64" }}>
                            Reference auto-generated if left blank. Status drives the dashboard active-projects KPI.
                        </p>
                    </div>
                    <button data-testid="project-form-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4 form-pane">
                    <Field label="Project name" required>
                        <input data-testid="project-form-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="fi" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Client" required>
                            <select data-testid="project-form-client" value={form.client_id || ""} onChange={(e) => set("client_id", e.target.value)} className="fi">
                                <option value="">Select…</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Reference (auto if blank)">
                            <input data-testid="project-form-ref" value={form.reference || ""} onChange={(e) => set("reference", e.target.value)} className="fi font-mono" placeholder="P-…" />
                        </Field>
                    </div>
                    {mode === "create" && (
                        <Field label="Project template (optional — copies phases, tasks, owners & required docs)">
                            <select data-testid="project-form-template" value={form.template_id || ""} onChange={(e) => set("template_id", e.target.value)} className="fi">
                                <option value="">No template — use standard 8-stage workflow</option>
                                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</option>)}
                            </select>
                            {chosenTemplate && (
                                <div data-testid="project-form-template-preview" className="mt-2 text-[11px] rounded-[8px] p-2.5 border" style={{ borderColor: "#E5E0DA", background: "#FCFBF9", color: "#6F6A64" }}>
                                    <span className="font-semibold text-[#2D2D2D]">{chosenTemplate.phases.length} phases:</span> {chosenTemplate.phases.map((p) => p.label).join(" → ")}
                                </div>
                            )}
                        </Field>
                    )}
                    {form.client_id && (
                        <div className="grid sm:grid-cols-2 gap-3">
                            <Field label="Linked sites">
                                <div data-testid="project-form-sites" className="border rounded-[8px] p-2 max-h-[120px] overflow-y-auto space-y-1" style={{ borderColor: "#E5E0DA" }}>
                                    {sites.length === 0 ? <span className="text-[11px] italic text-[#9A968F]">No sites for this client.</span> : sites.map((s) => (
                                        <label key={s.id} className="flex items-center gap-2 text-[12px] text-[#2D2D2D] cursor-pointer">
                                            <input type="checkbox" data-testid={`project-form-site-${s.id}`} checked={(form.site_ids || []).includes(s.id)} onChange={() => toggleId("site_ids", s.id)} />
                                            {s.name}
                                        </label>
                                    ))}
                                </div>
                            </Field>
                            <Field label="Linked buildings">
                                <div data-testid="project-form-buildings" className="border rounded-[8px] p-2 max-h-[120px] overflow-y-auto space-y-1" style={{ borderColor: "#E5E0DA" }}>
                                    {buildings.length === 0 ? <span className="text-[11px] italic text-[#9A968F]">No buildings for this client.</span> : buildings.map((b) => (
                                        <label key={b.id} className="flex items-center gap-2 text-[12px] text-[#2D2D2D] cursor-pointer">
                                            <input type="checkbox" data-testid={`project-form-building-${b.id}`} checked={(form.building_ids || []).includes(b.id)} onChange={() => toggleId("building_ids", b.id)} />
                                            {b.name}
                                        </label>
                                    ))}
                                </div>
                            </Field>
                        </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Project type(s)">
                            <div data-testid="project-form-types" className="border rounded-[8px] p-2 max-h-[120px] overflow-y-auto space-y-1" style={{ borderColor: "#E5E0DA" }}>
                                {typeOptions.length === 0 ? <span className="text-[11px] italic text-[#9A968F]">No project types configured yet.</span> : typeOptions.map((t) => (
                                    <label key={t.id} className="flex items-center gap-2 text-[12px] text-[#2D2D2D] cursor-pointer">
                                        <input type="checkbox" data-testid={`project-form-type-${t.id}`} checked={(form.project_types || []).includes(t.name)} onChange={() => toggleId("project_types", t.name)} />
                                        {t.name}
                                    </label>
                                ))}
                            </div>
                            <NewTypeInline onAdded={(t) => { setTypeOptions((prev) => [...prev, t]); toggleId("project_types", t.name); }} />
                        </Field>
                        <Field label="Lead consultant">
                            <input data-testid="project-form-lead" value={form.lead_consultant || ""} onChange={(e) => set("lead_consultant", e.target.value)} className="fi" />
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="RIBA stage">
                            <select data-testid="project-form-riba" value={form.riba_stage || "N/A"} onChange={(e) => set("riba_stage", e.target.value)} className="fi">
                                {RIBA_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="CDM role">
                            <select data-testid="project-form-cdm" value={form.cdm_role || "N/A"} onChange={(e) => set("cdm_role", e.target.value)} className="fi">
                                {CDM_ROLES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="F10 number">
                            <input data-testid="project-form-f10" value={form.f10_number || ""} onChange={(e) => set("f10_number", e.target.value)} className="fi font-mono" />
                        </Field>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Start date">
                            <input data-testid="project-form-start" type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="fi" />
                        </Field>
                        <Field label="Target date">
                            <input data-testid="project-form-target" type="date" value={form.target_date || ""} onChange={(e) => set("target_date", e.target.value)} className="fi" />
                        </Field>
                        <Field label="Value (£)">
                            <input data-testid="project-form-value" type="number" value={form.value_gbp ?? ""} onChange={(e) => set("value_gbp", e.target.value)} className="fi" />
                        </Field>
                    </div>
                    <Field label="Status">
                        <select data-testid="project-form-status" value={form.status || "Active"} onChange={(e) => set("status", e.target.value)} className="fi">
                            {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </select>
                    </Field>
                    {clients.find((c) => c.id === form.client_id)?.weekly_reports_enabled && (
                        <div className="rounded-[10px] border p-3.5 space-y-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                            <div className="text-[11px] font-bold uppercase tracking-[.06em] text-[#8B867E]">Weekly Site Report</div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <Field label="Contract reference">
                                    <input data-testid="project-form-contract-ref" value={form.contract_reference || ""} onChange={(e) => set("contract_reference", e.target.value)} className="fi" />
                                </Field>
                                <Field label="Client representative">
                                    <input data-testid="project-form-client-rep" value={form.client_representative || ""} onChange={(e) => set("client_representative", e.target.value)} className="fi" />
                                </Field>
                            </div>
                            <Field label="Distribution (names / emails who receive the report)">
                                <textarea data-testid="project-form-wsr-distribution" rows={2} value={form.wsr_distribution || ""} onChange={(e) => set("wsr_distribution", e.target.value)} className="fi" />
                            </Field>
                            <Field label="WSR reviewers override (optional — leave empty to use the client's default list)">
                                <WsrReviewerPicker clientId={form.client_id} value={form.wsr_reviewer_user_ids_override || []} onChange={(v) => set("wsr_reviewer_user_ids_override", v)} />
                            </Field>
                            <label className="flex items-center gap-2 text-[12px] text-[#2D2D2D] cursor-pointer pt-1">
                                <input type="checkbox" data-testid="project-form-wsr-excluded"
                                       checked={!!form.wsr_excluded} onChange={(e) => set("wsr_excluded", e.target.checked)} />
                                Exclude from Weekly Site Reports (dormant/inactive site — no report required)
                            </label>
                        </div>
                    )}
                    <Field label="Notes">
                        <textarea data-testid="project-form-notes" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="fi" />
                    </Field>

                    {mode === "edit" && initial?.id && (
                        <ProjectLogoField projectId={initial.id} hasLogo={!!form.logo_url}
                                          onChange={(url) => set("logo_url", url)} />
                    )}

                    <div className="pt-3 border-t" style={{ borderColor: "#F0EDE8" }}>
                        <ProjectContactsEditor
                            projectId={mode === "edit" ? initial?.id : null}
                            value={form.contacts || []}
                            onChange={(c) => set("contacts", c)}
                            canEdit />
                    </div>
                </div>

                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex-1" />
                    <button onClick={onClose} disabled={busy} data-testid="project-form-cancel"
                        className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white"
                        style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="project-form-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {busy ? "Saving…" : "Save project"}
                    </button>
                </div>

                <style>{`
                    .form-pane .fi {
                        width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA;
                        border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none;
                        transition: border-color .12s, box-shadow .12s;
                    }
                    .form-pane .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                `}</style>
            </div>
        </div>
    );
}

// Inline "add a project type if it's missing" — used from the New/Edit
// project dialog's Project type(s) checklist, so staff never have to leave
// the form to go to Settings → Project Types first.
function NewTypeInline({ onAdded }) {
    const [adding, setAdding] = useState(false);
    const [name, setName] = useState("");
    const [busy, setBusy] = useState(false);
    async function add() {
        const n = name.trim();
        if (!n) return;
        setBusy(true);
        try {
            const { data } = await api.post("/project-types", { name: n });
            onAdded(data);
            setName(""); setAdding(false);
            toast.success(`Added project type "${data.name}"`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not add type"); }
        finally { setBusy(false); }
    }
    if (!adding) {
        return (
            <button type="button" data-testid="project-form-type-add-toggle" onClick={() => setAdding(true)}
                className="mt-1.5 text-[11.5px] font-semibold inline-flex items-center gap-1" style={{ color: "var(--brand-accent, #C75A1B)" }}>
                <Plus className="w-3 h-3" /> Add a project type
            </button>
        );
    }
    return (
        <div className="mt-1.5 flex items-center gap-1.5">
            <input data-testid="project-form-type-add-input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } if (e.key === "Escape") setAdding(false); }}
                placeholder="New type name" className="fi flex-1 !py-1.5" />
            <button type="button" data-testid="project-form-type-add-save" onClick={add} disabled={busy || !name.trim()}
                className="px-2.5 py-1.5 rounded-[7px] text-[11.5px] font-semibold text-white disabled:opacity-60" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                {busy ? "…" : "Add"}
            </button>
            <button type="button" onClick={() => setAdding(false)} className="px-2 py-1.5 text-[11.5px] font-semibold" style={{ color: "#6F6A64" }}>Cancel</button>
        </div>
    );
}

// Multi-select filter used in the Filters popover — each field (Status,
// Stage, Consultant, Type, Flag) can now have more than one value ticked at
// once (e.g. Type = "Fire Safety" AND "Fire Risk Assessment"), matched with
// OR logic against each project's value for that field.
function FilterMultiSelect({ label, testId, options, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const labelText = selected.length === 0 ? `All ${label.toLowerCase()}`
        : selected.length === 1 ? (options.find(([v]) => v === selected[0])?.[1] || selected[0])
        : `${selected.length} selected`;
    function toggle(v) {
        onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
    }
    return (
        <div className="relative">
            <span className="text-[10.5px] font-bold uppercase tracking-wide mb-1 block" style={{ color: "#6F6A64" }}>{label}</span>
            <button type="button" data-testid={testId} onClick={() => setOpen((v) => !v)}
                className="w-full text-left bg-white border rounded-[8px] px-2.5 py-1.5 text-[12.5px] font-semibold outline-none truncate flex items-center justify-between gap-1"
                style={{ borderColor: "#E5E0DA", color: selected.length ? "#1F1F1F" : "#3e4b63" }}>
                <span className="truncate">{labelText}</span>
                {selected.length > 0 && (
                    <span onClick={(e) => { e.stopPropagation(); onChange([]); }} className="shrink-0 text-[10px] font-bold px-1.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>×</span>
                )}
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div data-testid={`${testId}-panel`} className="absolute z-50 mt-1 left-0 right-0 bg-white border rounded-[8px] shadow-brand p-2 space-y-0.5 max-h-[180px] overflow-y-auto"
                        style={{ borderColor: "#E5E0DA" }}>
                        {options.length === 0 ? <span className="text-[11px] italic text-[#9A968F] px-1">No options</span> : options.map(([v, l]) => (
                            <label key={v} className="flex items-center gap-2 text-[12px] text-[#2D2D2D] cursor-pointer px-1 py-0.5 rounded hover:bg-[#F6F4F0]">
                                <input type="checkbox" data-testid={`${testId}-option-${v}`} checked={selected.includes(v)} onChange={() => toggle(v)} />
                                {l}
                            </label>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// --- Inline "Excel-style" editable cells for the projects list ---
function CellSelect({ testId, value, onChange, options }) {
    return (
        <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
            className="w-full bg-white border rounded-[7px] px-1.5 py-1 text-[12px] outline-none focus:border-[var(--brand-accent,#C75A1B)]"
            style={{ borderColor: "#E5E0DA" }}>
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
    );
}

function CellInput({ testId, value, onCommit, type = "text", placeholder, prefix }) {
    const [v, setV] = useState(value ?? "");
    useEffect(() => { setV(value ?? ""); }, [value]);
    const commit = () => { if (String(v) !== String(value ?? "")) onCommit(v); };
    return (
        <div className="flex items-center gap-0.5">
            {prefix && <span className="text-[11px]" style={{ color: "#9A968F" }}>{prefix}</span>}
            <input data-testid={testId} type={type} value={v} placeholder={placeholder}
                onChange={(e) => setV(e.target.value)} onBlur={commit}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                className="w-full bg-white border rounded-[7px] px-1.5 py-1 text-[12px] outline-none focus:border-[var(--brand-accent,#C75A1B)]"
                style={{ borderColor: "#E5E0DA" }} />
        </div>
    );
}

function BulkValueControl({ field, value, onChange, consultants, typeNames }) {
    const cls = "bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none min-w-[150px]";
    const style = { borderColor: "#E5E0DA" };
    if (field === "status")
        return <select data-testid="projects-bulk-value" value={value} onChange={(e) => onChange(e.target.value)} className={cls} style={style}>
            <option value="">Select…</option>{PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>;
    if (field === "workflow_stage")
        return <select data-testid="projects-bulk-value" value={value} onChange={(e) => onChange(e.target.value)} className={cls} style={style}>
            <option value="">Select…</option>{STAGE_ORDER.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}</select>;
    if (field === "project_types")
        return <TypeMultiSelect testId="projects-bulk-value" options={typeNames} selected={Array.isArray(value) ? value : []} onChange={onChange} placeholder="Choose type(s)…" />;
    if (field === "target_date")
        return <input data-testid="projects-bulk-value" type="date" value={value} onChange={(e) => onChange(e.target.value)} className={cls} style={style} />;
    if (field === "value_gbp")
        return <input data-testid="projects-bulk-value" type="number" placeholder="£" value={value} onChange={(e) => onChange(e.target.value)} className={cls} style={style} />;
    // lead_consultant
    return <input data-testid="projects-bulk-value" list="bulk-consultants" placeholder="Lead consultant" value={value} onChange={(e) => onChange(e.target.value)} className={cls} style={style} />;
}

// Compact dropdown checkbox-list used for inline-edit & bulk-edit Type controls
// (the create/edit dialog uses a static checkbox list instead — more room there).
function TypeMultiSelect({ testId, options, selected, onChange, placeholder = "Select types…" }) {
    const [open, setOpen] = useState(false);
    const label = selected.length === 0 ? placeholder : selected.join(", ");
    function toggle(name) {
        onChange(selected.includes(name) ? selected.filter((n) => n !== name) : [...selected, name]);
    }
    return (
        <div className="relative min-w-[150px]">
            <button type="button" data-testid={testId} onClick={() => setOpen((v) => !v)}
                className="w-full text-left bg-white border rounded-[7px] px-2 py-1 text-[12px] truncate outline-none focus:border-[var(--brand-accent,#C75A1B)]"
                style={{ borderColor: "#E5E0DA", color: selected.length ? "#1F1F1F" : "#9A968F" }}>
                {label}
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div data-testid={`${testId}-panel`} className="absolute z-50 mt-1 left-0 min-w-[200px] bg-white border rounded-[8px] shadow-brand p-2 space-y-1 max-h-[220px] overflow-y-auto"
                        style={{ borderColor: "#E5E0DA" }}>
                        {options.length === 0 ? <span className="text-[11px] italic text-[#9A968F] px-1">No types configured</span> : options.map((name) => (
                            <label key={name} className="flex items-center gap-2 text-[12px] text-[#2D2D2D] cursor-pointer px-1 py-0.5 rounded hover:bg-[#F6F4F0]">
                                <input type="checkbox" checked={selected.includes(name)} onChange={() => toggle(name)} />
                                {name}
                            </label>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}
